import React, { useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import moment from 'moment';

const TokenVolumeGraph = ({
  data,
  monthlyAverage,
  t,
  totalLabel,
  yAxisLabel,
  dailySeriesLabel,
  whatsappSeriesLabel,
  webSeriesLabel,
  averageSeriesLabel,
  showBarValueLabels = true
}) => {
  const safeData = data || { daysInMonth: 0, dailyUsage: {}, dailyUsageByChannel: {} }

  const parseNumericValue = (value) => {
    if (typeof value === 'number') return value
    if (typeof value === 'string') {
      const normalized = value.replace(/,/g, '').trim()
      const parsed = Number(normalized)
      return Number.isFinite(parsed) ? parsed : null
    }
    return null
  }

  // 1. Preparar los datos para el gráfico
  const days = [];
  const usageValues = [];
  const whatsappValues = [];
  const webValues = [];
  const monthlyAverageValue = parseNumericValue(monthlyAverage)
  const dailyUsageByChannel = safeData.dailyUsageByChannel || {}
  const hasChannelBreakdown = Boolean(
    dailyUsageByChannel.whatsapp || dailyUsageByChannel.web
  )
  const resolvedTotalLabel = totalLabel || t('totalConversations')
  const resolvedYAxisLabel = yAxisLabel || t('yAxis')
  const resolvedDailySeriesLabel = dailySeriesLabel || t('dailyConversations')
  const resolvedWhatsappSeriesLabel = whatsappSeriesLabel || t('whatsappConversations')
  const resolvedWebSeriesLabel = webSeriesLabel || t('webConversations')
  const resolvedAverageSeriesLabel = averageSeriesLabel || t('monthlyAverageElapsedDays')

  useEffect(() => {
    moment.locale(t.language === 'en' ? 'en' : 'es');
  }, [t.language])

  if (!data) return null
  
  for (let day = 1; day <= safeData.daysInMonth; day++) {
    days.push(day);

    const totalValue = parseNumericValue(safeData.dailyUsage[day]) || 0
    const whatsappValue = parseNumericValue(dailyUsageByChannel?.whatsapp?.[day]) || 0
    const webValue = parseNumericValue(dailyUsageByChannel?.web?.[day]) || 0

    usageValues.push(totalValue);
    whatsappValues.push(whatsappValue);
    webValues.push(webValue);
  }

  const monthlyAverageValues = days.map(() => monthlyAverageValue)

  // 2. Configuración del gráfico
  const option = {
    title: {
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: function(params) {
        const day = Number(params[0]?.axisValue || 0)
        const totalValue = Number.isFinite(usageValues[day - 1]) ? usageValues[day - 1] : 0
        const rows = params.map(param => {
          const seriesValue = Number.isFinite(param.value) ? param.value : 0
          return `${param.marker} ${param.seriesName}: ${seriesValue}`
        })

        return `${t('xAxis')} ${params[0]?.name}<br/><strong>${resolvedTotalLabel}: ${totalValue}</strong><br/>${rows.join('<br/>')}`
      }
    },
    xAxis: {
      type: 'category',
      name: t('xAxis'),
      data: days,
      axisLabel: {
        interval: 0, // Mostrar todos los días
        rotate: 0, // Rotar etiquetas si hay muchos días
        fontSize: 10
      },
      axisLine: {
        lineStyle: {
          color: '#333'
        }
      }
    },
    yAxis: {
      type: 'value',
      name: resolvedYAxisLabel,
      axisLine: {
        show: true
      },
      splitLine: {
        lineStyle: {
          type: 'dashed'
        }
      }
    },
    series: [
      ...(hasChannelBreakdown
        ? [
            {
              name: resolvedWhatsappSeriesLabel,
              type: 'bar',
              stack: 'channels',
              data: whatsappValues,
              itemStyle: { color: '#69B75C' },
              barWidth: '70%'
            },
            {
              name: resolvedWebSeriesLabel,
              type: 'bar',
              stack: 'channels',
              data: webValues,
              itemStyle: { color: '#00A4E0' },
              barWidth: '70%'
            }
          ]
        : [{
            name: resolvedDailySeriesLabel,
            type: 'bar',
            data: usageValues,
            itemStyle: { color: "#00a4e0" },
            barWidth: '70%',
            label: {
              show: showBarValueLabels,
              position: 'top',
              formatter: function(params) {
                return params.value > 0 ? params.value : '';
              },
              fontSize: 12,
              color: '#333'
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }]),
      ...(Number.isFinite(monthlyAverageValue)
        ? [{
            name: resolvedAverageSeriesLabel,
            type: 'line',
            data: monthlyAverageValues,
            symbol: 'none',
            lineStyle: {
              width: 2,
              type: 'dashed',
              color: '#515458'
            },
            itemStyle: {
              color: '#515458'
            }
          }]
        : [])
    ],
    legend: {
      show: true,
      top: 4
    },
    grid: {
      top: 54,
      right: 4,
      bottom: 24,
      left: '5%',
      containLabel: true
    }
  };

  return (
    <div style={{ width: '100%', height: '310px' }}>
      <ReactECharts
        option={option}
        style={{ height: '100%', width: '100%' }}
        className="token-usage-chart"
      />
    </div>
  );
}

export default TokenVolumeGraph;